// =============================================================================
// SimSense Marketplace — Publish Setup Modal (OT-282)
// =============================================================================
// Multi-step flow: Select Session → Setup Details → Pricing → Review & Publish

import { useEffect, useState } from "react";
import { api } from "../../utils/api";
import { formatLapTime } from "../../utils/formatters";
import "./PublishModal.css";

interface SessionOption {
  id: string;
  trackName: string;
  carName: string;
  sessionDate: string;
  bestLapTime?: number;
  lapCount?: number;
  stintCount?: number;
}

interface PublishFormData {
  sessionId: string;
  session: SessionOption | null;
  setupName: string;
  type: "race" | "qualifying" | "baseline";
  tags: string[];
  priceCents: number;
  isFree: boolean;
}

const INITIAL_FORM: PublishFormData = {
  sessionId: "",
  session: null,
  setupName: "",
  type: "race",
  tags: [],
  priceCents: 0,
  isFree: true,
};

const SETUP_TYPES = [
  { value: "race", label: "Race", desc: "Optimized for race conditions and tire management" },
  { value: "qualifying", label: "Qualifying", desc: "Maximum speed for a single fast lap" },
  { value: "baseline", label: "Baseline", desc: "Starting point for further tuning" },
] as const;

const COMMON_TAGS = ["Low Downforce", "High Downforce", "Wet", "Fixed", "Open", "Short Run", "Long Run", "Oval", "Road"];

interface PublishModalProps {
  onClose: () => void;
  onPublished?: () => void;
}

export function PublishModal({ onClose, onPublished }: PublishModalProps) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<PublishFormData>(INITIAL_FORM);
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  // Escape to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Fetch user sessions for step 1
  useEffect(() => {
    async function fetchSessions() {
      try {
        const data = await api<any[]>("/api/sessions?limit=50");
        setSessions(data.map((s: any) => ({
          id: s.id,
          trackName: s.trackName,
          carName: s.carName,
          sessionDate: s.sessionDate,
          bestLapTime: s.bestLapTime,
          lapCount: s.lapCount,
          stintCount: s.stintCount,
        })));
      } catch {
        setSessions([]);
      } finally {
        setLoadingSessions(false);
      }
    }
    fetchSessions();
  }, []);

  const filteredSessions = sessions.filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return s.trackName.toLowerCase().includes(q) || s.carName.toLowerCase().includes(q);
  });

  function selectSession(s: SessionOption) {
    setPublishError(null);
    setForm(prev => ({
      ...prev,
      sessionId: s.id,
      session: s,
      setupName: prev.setupName || `${s.carName} — ${s.trackName}`,
    }));
    setStep(2);
  }

  function addTag(tag: string) {
    const t = tag.trim();
    if (t && !form.tags.includes(t) && form.tags.length < 8) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, t] }));
    }
    setTagInput("");
  }

  function removeTag(tag: string) {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  }

  async function handlePublish() {
    if (!form.sessionId || !form.setupName.trim()) {
      setPublishError("Select a proof session and setup name before publishing.");
      return;
    }

    setPublishing(true);
    setPublishError(null);
    try {
      await api("/api/marketplace/publish", {
        method: "POST",
        body: JSON.stringify({
          session_id: form.sessionId,
          setup_name: form.setupName.trim(),
          type: form.type,
          tags: form.tags,
          price_cents: form.isFree ? 0 : form.priceCents,
          is_free: form.isFree,
        }),
      });
      onPublished?.();
      onClose();
    } catch (error) {
      setPublishError(error instanceof Error ? error.message : "Failed to publish setup.");
    } finally {
      setPublishing(false);
    }
  }

  const canProceedStep2 = form.setupName.trim().length > 0;
  const canProceedStep3 = form.isFree || form.priceCents >= 99;

  return (
    <div className="publish-overlay" onClick={onClose}>
      <div className="publish-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="publish-header">
          <div className="publish-header-left">
            <h2>Publish Setup</h2>
            <div className="publish-steps">
              {["Session", "Details", "Pricing", "Review"].map((label, i) => (
                <div key={i} className={`publish-step ${step === i + 1 ? "active" : ""} ${step > i + 1 ? "done" : ""}`}>
                  <span className="publish-step-num">{step > i + 1 ? "\u2713" : i + 1}</span>
                  <span className="publish-step-label">{label}</span>
                </div>
              ))}
            </div>
          </div>
          <button className="publish-close" onClick={onClose}>&times;</button>
        </div>

        {/* Step 1: Select Session */}
        {step === 1 && (
          <div className="publish-body">
            <div className="publish-step-title">Select a Telemetry Session</div>
            <p className="publish-step-desc">
              Choose the session that proves this setup works. The lap times and conditions
              will be shown to buyers as telemetry-backed proof.
            </p>

            <input
              className="publish-search"
              type="text"
              placeholder="Search by track or car..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />

            <div className="publish-session-list">
              {loadingSessions && <div className="publish-empty">Loading sessions...</div>}
              {!loadingSessions && filteredSessions.length === 0 && (
                <div className="publish-empty">No sessions found. Upload some telemetry first.</div>
              )}
              {filteredSessions.map(s => (
                <button
                  key={s.id}
                  className={`publish-session-card ${form.sessionId === s.id ? "selected" : ""}`}
                  onClick={() => selectSession(s)}
                >
                  <div className="publish-session-main">
                    <div className="publish-session-track">{s.trackName}</div>
                    <div className="publish-session-car">{s.carName}</div>
                  </div>
                  <div className="publish-session-meta">
                    {s.bestLapTime && s.bestLapTime > 0 && (
                      <span className="publish-session-lap">{formatLapTime(s.bestLapTime)}</span>
                    )}
                    <span className="publish-session-date">{new Date(s.sessionDate).toLocaleDateString()}</span>
                    {s.lapCount != null && s.lapCount > 0 && (
                      <span className="publish-session-laps">{s.lapCount} laps</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Setup Details */}
        {step === 2 && (
          <div className="publish-body">
            <div className="publish-step-title">Setup Details</div>
            <p className="publish-step-desc">
              Describe your setup so drivers know what to expect.
            </p>

            <div className="publish-form-group">
              <label className="publish-label">Setup Name</label>
              <input
                className="publish-input"
                type="text"
                value={form.setupName}
                onChange={e => setForm(prev => ({ ...prev, setupName: e.target.value }))}
                placeholder="e.g., GT3 Spa Low Downforce Race"
                maxLength={80}
              />
            </div>

            <div className="publish-form-group">
              <label className="publish-label">Setup Type</label>
              <div className="publish-option-group publish-option-group-row">
                {SETUP_TYPES.map(t => (
                  <button
                    key={t.value}
                    className={`publish-option ${form.type === t.value ? "selected" : ""}`}
                    onClick={() => setForm(prev => ({ ...prev, type: t.value }))}
                  >
                    <span className="publish-option-label">{t.label}</span>
                    <span className="publish-option-desc">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="publish-form-group">
              <label className="publish-label">Tags</label>
              <div className="publish-tags-input">
                {form.tags.map(t => (
                  <span key={t} className="publish-tag">
                    {t}
                    <button onClick={() => removeTag(t)}>&times;</button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
                  placeholder={form.tags.length < 8 ? "Add a tag..." : ""}
                  disabled={form.tags.length >= 8}
                />
              </div>
              <div className="publish-common-tags">
                {COMMON_TAGS.filter(t => !form.tags.includes(t)).map(t => (
                  <button key={t} className="publish-common-tag" onClick={() => addTag(t)}>{t}</button>
                ))}
              </div>
            </div>

            <div className="publish-nav">
              <button className="publish-btn-secondary" onClick={() => setStep(1)}>Back</button>
              <button className="publish-btn-primary" disabled={!canProceedStep2} onClick={() => setStep(3)}>
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Pricing */}
        {step === 3 && (
          <div className="publish-body">
            <div className="publish-step-title">Set Your Price</div>
            <p className="publish-step-desc">
              Free setups get more downloads. Paid setups earn you revenue.
            </p>

            <div className="publish-pricing-options">
              <button
                className={`publish-price-card ${form.isFree ? "selected" : ""}`}
                onClick={() => setForm(prev => ({ ...prev, isFree: true, priceCents: 0 }))}
              >
                <div className="publish-price-card-title">Free</div>
                <div className="publish-price-card-amount">$0</div>
                <div className="publish-price-card-desc">
                  Share with the community. Maximum visibility and downloads.
                </div>
              </button>

              <button
                className={`publish-price-card ${!form.isFree ? "selected" : ""}`}
                onClick={() => setForm(prev => ({ ...prev, isFree: false, priceCents: prev.priceCents || 299 }))}
              >
                <div className="publish-price-card-title">Paid</div>
                <div className="publish-price-card-amount">
                  ${(form.priceCents / 100).toFixed(2)}
                </div>
                <div className="publish-price-card-desc">
                  Set a price for your setup. Buyers pay once to download.
                </div>
              </button>
            </div>

            {!form.isFree && (
              <div className="publish-form-group" style={{ marginTop: "20px" }}>
                <label className="publish-label">Price (USD)</label>
                <div className="publish-price-input-row">
                  <span className="publish-price-prefix">$</span>
                  <input
                    className="publish-input publish-price-input"
                    type="number"
                    min="0.99"
                    max="50.00"
                    step="0.01"
                    value={(form.priceCents / 100).toFixed(2)}
                    onChange={e => {
                      const cents = Math.round(parseFloat(e.target.value || "0") * 100);
                      setForm(prev => ({ ...prev, priceCents: Math.max(99, Math.min(5000, cents)) }));
                    }}
                  />
                </div>
                <div className="publish-price-hint">
                  Min $0.99, max $50.00
                </div>
              </div>
            )}

            <div className="publish-nav">
              <button className="publish-btn-secondary" onClick={() => setStep(2)}>Back</button>
              <button className="publish-btn-primary" disabled={!canProceedStep3} onClick={() => setStep(4)}>
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review & Publish */}
        {step === 4 && (
          <div className="publish-body">
            <div className="publish-step-title">Review & Publish</div>
            <p className="publish-step-desc">
              Check everything looks good before publishing to the marketplace.
            </p>

            <div className="publish-review-card">
              <div className="publish-review-section">
                <div className="publish-review-label">Proof Session</div>
                <div className="publish-review-value">
                  {form.session?.trackName} — {form.session?.carName}
                  {form.session?.bestLapTime && form.session.bestLapTime > 0 && (
                    <span className="publish-review-lap">{formatLapTime(form.session.bestLapTime)}</span>
                  )}
                </div>
              </div>

              <div className="publish-review-section">
                <div className="publish-review-label">Setup Name</div>
                <div className="publish-review-value">{form.setupName}</div>
              </div>

              <div className="publish-review-row">
                <div className="publish-review-section">
                  <div className="publish-review-label">Type</div>
                  <div className="publish-review-value">{form.type}</div>
                </div>
                <div className="publish-review-section">
                  <div className="publish-review-label">Price</div>
                  <div className="publish-review-value">
                    {form.isFree ? "Free" : `$${(form.priceCents / 100).toFixed(2)}`}
                  </div>
                </div>
              </div>

              {form.tags.length > 0 && (
                <div className="publish-review-section">
                  <div className="publish-review-label">Tags</div>
                  <div className="publish-review-tags">
                    {form.tags.map(t => <span key={t} className="publish-review-tag">{t}</span>)}
                  </div>
                </div>
              )}
            </div>

            {publishError && (
              <div className="publish-empty" style={{ marginBottom: "16px", color: "var(--danger, #d9534f)" }}>
                {publishError}
              </div>
            )}

            <div className="publish-nav">
              <button className="publish-btn-secondary" onClick={() => setStep(3)}>Back</button>
              <button
                className="publish-btn-primary publish-btn-publish"
                disabled={publishing || !form.sessionId || !form.setupName.trim()}
                onClick={handlePublish}
              >
                {publishing ? "Publishing..." : "Publish to Marketplace"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
